import type { Router } from 'vue-router'
import type { IconComponent } from './IconComponent'

export type GatshipSearchOperator = 'equals' | 'contains' | 'like'

/**
 * The contexts the global search dialog can search in. Shared contract:
 * feature packages provide search functions keyed by context without
 * importing the global-search feature.
 */
export const searchContextItems = {
  global: 'global',
  client: 'client',
  vendors: 'vendors',
  otherParties: 'otherParties',
  contacts: 'contacts',
  navigation: 'navigation'
} as const

export type SearchContextType =
  (typeof searchContextItems)[keyof typeof searchContextItems]

export interface SearchResult<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Metadata extends Record<string, any> = Record<string, any>
> {
  /** The id of the search result. */
  id: string
  /** The title of the search result. */
  title: string
  /** The type of context the search result belongs to. */
  contextType: SearchContextType
  subTitle?: string
  icon?: IconComponent
  /** The actions a user can perform on the search result. */
  actions: {
    /** The action to be performed when the search result is selected. */
    selected: (router: Omit<Router, 'install'>) => void
  }
  /** The values that can be used to search for this result. If not provided, the title will be used. */
  searchValues?: string[]
  metadata?: Metadata
}

export interface SearchFunctionOptions {
  /** Aborts the underlying request when a newer search supersedes this one. */
  signal?: AbortSignal
}

/** A provider-supplied function resolving search results for one context. */
export type SearchFunction = (
  search: string | undefined,
  options?: SearchFunctionOptions
) => Promise<SearchResult[]>

export type SearchFunctionMap = Partial<
  Record<SearchContextType, SearchFunction>
>
